import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ModuleTile from './ModuleTile';
import { dashboardModules, DashboardModule } from '../../config/dashboardModules';
import authService from '../../services/auth.service';
import { filterMenusByRole, MenuItem as FilterMenuItem } from '../../utils/menuFilter';

interface ModuleLauncherProps {
  extraModules?: DashboardModule[];
}

function isModuleAllowed(module: DashboardModule, allowedMenuIds: Set<string>): boolean {
  if (module.id === 'dashboard') return true;
  if (allowedMenuIds.has(module.id)) return true;
  // Quick-access tiles reuse parent menu id (e.g. requests under operations)
  return false;
}

const ModuleLauncher: React.FC<ModuleLauncherProps> = ({ extraModules = [] }) => {
  const { t } = useTranslation();
  const [userRole, setUserRole] = useState<any>(null);

  useEffect(() => {
    const loadRole = async () => {
      try {
        const user = authService.getCurrentUser();
        if (!user) return;
        const me = await authService.getMe();
        setUserRole(me?.role || user.role || null);
      } catch {
        const user = authService.getCurrentUser();
        setUserRole(user?.role || null);
      }
    };
    loadRole();
  }, []);

  const allowedMenuIds = useMemo(() => {
    const menuSkeleton: FilterMenuItem[] = [
      { id: 'dashboard', textKey: 'navigation.dashboard', path: '/dashboard' },
      { id: 'reports', textKey: 'navigation.reports', children: [{ id: 'r', textKey: '', path: '/reports' }] },
      { id: 'operations', textKey: 'navigation.operations', children: [{ id: 'o', textKey: '', path: '/operations/overview' }] },
      { id: 'organization', textKey: 'navigation.organization', children: [{ id: 'h', textKey: '', path: '/organization/overview' }] },
      { id: 'sales-crm', textKey: 'navigation.salesCRM', children: [{ id: 's', textKey: '', path: '/sales/overview' }] },
      { id: 'marketing', textKey: 'navigation.marketing', children: [{ id: 'm', textKey: '', path: '/marketing/overview' }] },
      { id: 'finance', textKey: 'navigation.finance', children: [{ id: 'f', textKey: '', path: '/finance/overview' }] },
      { id: 'settings', textKey: 'navigation.settings', children: [{ id: 'st', textKey: '', path: '/settings' }] },
      { id: 'insurance-broker', textKey: 'navigation.insuranceBroker', path: '/insurance-broker' },
    ];
    const filtered = filterMenusByRole(menuSkeleton, userRole);
    return new Set(filtered.map((m) => m.id));
  }, [userRole]);

  const visibleModules = useMemo(() => {
    const all = [...dashboardModules, ...extraModules];
    const seen = new Set<string>();
    return all.filter((mod) => {
      const key = `${mod.path}-${mod.titleKey}`;
      if (seen.has(key)) return false;
      if (!isModuleAllowed(mod, allowedMenuIds)) return false;
      seen.add(key);
      return true;
    });
  }, [allowedMenuIds, extraModules]);

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2.5 }}>
        {t('dashboard.applications')}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            md: 'repeat(4, 1fr)',
            lg: 'repeat(6, 1fr)',
          },
          gap: 2,
        }}
      >
        {visibleModules.map((mod) => (
          <ModuleTile
            key={`${mod.path}-${mod.titleKey}`}
            title={t(mod.titleKey)}
            icon={mod.icon}
            color={mod.color}
            bgLight={mod.bgLight}
            path={mod.path}
          />
        ))}
      </Box>
    </Box>
  );
};

export default ModuleLauncher;
